import { quizMarkBody, quizMarkProblem, type QuizMarkDraft } from "./quiz-mark-model";
import { a3Copy } from "@/components/assignments/a3-copy";
import { resolveScale } from "@/components/assignments/work/scale/model";
import { hasLevels, levelLines, levelMark, type LevelAward } from "@/components/assignments/work/rubric-levels";
import type { GradingScale } from "@/components/assignments/work/scale/types";
import type { BoxState } from "@/components/ui/box.schema";
/**
 * Review's words and choices, kept apart from the box so each rule reads and
 * tests on its own (board 3; agreement sections 3, 7, 8 and 11; LANGUAGE §4).
 * Nothing here chooses a judgement: a draft starts at rest and only the
 * teacher's typing moves it.
 */
import type {
  AssessmentKind,
  FeedbackOutcome,
  JudgementItem,
  JudgementItemResult,
  JudgementView,
  ReleaseChoice,
  ReleaseLinePerson,
  ReleaseResult,
  ReleaseRound,
  FormerRecipientRow,
  ReviewLearnerRow,
  RubricCriterion,
} from "@/lib/api/assignments";
import type { SessionAction, SessionWorkspace, TeachingSessionAssignment } from "@/lib/api/sessionPortal";

export const PREPARE_ACTIONS: readonly SessionAction[] = ["publish_session_plan", "publish_session_resource", "create_session_plan", "edit_session_plan"];

export const CLOSE_ACTIONS: readonly SessionAction[] = ["close_session", "complete_attendance", "update_roll", "take_attendance"];

/** The work Review holds: this session's published or closed work. */
export function reviewWork(workspace: SessionWorkspace): TeachingSessionAssignment[] {
  return ((workspace.plan.unit?.assignments ?? []) as TeachingSessionAssignment[])
    .filter(work => "coverage" in work && (work.status === "published" || work.status === "closed"));
}

export interface ReviewDraft {
  quiz?: QuizMarkDraft;
  outcome: FeedbackOutcome | null;
  score: string;
  criteria: Record<string, string>;
  body: string;
  release: boolean;
  /** The judgement on record when this draft began; another one landing is "judged elsewhere". */
  basis: string | null;
  selectedScaleRowId?: string;
  /** A level rubric's chosen level per criterion, and any award other than its level's points. */
  levels?: Record<string, string>;
  awards?: Record<string, LevelAward>;
}

export const emptyDraft = (basis: string | null = null): ReviewDraft =>
  ({ outcome: null, score: "", criteria: {}, body: "", release: true, basis, levels: {}, awards: {} });

/** A graded mark of a level rubric: recorded held through the marks route, never the batch. */
export const marksByLevel = (draft: Pick<ReviewDraft, "outcome">, terms: Pick<ReviewTerms, "rubric">) =>
  draft.outcome === "graded" && hasLevels(terms.rubric);

export const marksIndividually = (draft: Pick<ReviewDraft, "outcome">, terms: Pick<ReviewTerms, "rubric" | "kind">) =>
  draft.outcome === "graded" && (terms.kind === "native_quiz" || hasLevels(terms.rubric));

/**
 * The held mark a level draft records under the judgement on record, naming the
 * marker's latest saved private draft revision, which the record then closes.
 */
export function markBody(draft: ReviewDraft, terms: ReviewTerms, draftRevision: number | null) {
  if (terms.kind === "native_quiz") return { expected_feedback_id: draft.basis, decision: "graded" as const, quiz_awards: quizMarkBody(draft.quiz), comment: draft.body.trim() || null, draft_revision: draftRevision };
  const lines = levelLines(terms.rubric ?? [], draft.levels ?? {}, draft.awards ?? {});
  return { expected_feedback_id: draft.basis, decision: "graded" as const, ...levelMark(lines, draft.awards ?? {}),
    comment: draft.body.trim() || null, draft_revision: draftRevision };
}

export interface ReviewTerms {
  kind: AssessmentKind;
  points: number | null;
  rubric: RubricCriterion[] | null;
  scale?: GradingScale | null;
  /** Why a return is refused (closed work or term), or null. */
  returnRefused: string | null;
}

/** The server resolves the terms of the version each attempt answered. */
export type ReviewRow = ReviewLearnerRow;

export function termsFor(row: Pick<ReviewRow, "attempt" | "attempt_terms">, current: ReviewTerms): ReviewTerms {
  const answered = row.attempt && row.attempt_terms;
  return answered ? { ...current, kind: answered.assessment_kind, points: answered.points, rubric: answered.rubric, scale: answered.scale ?? null } : current;
}

export function answeredTermsWords(row: ReviewRow, current: ReviewTerms): string {
  const answered = termsFor(row, current);
  const samePoints = answered.points == null ? current.points == null : current.points != null && Number(answered.points) === Number(current.points);
  if (!row.attempt_terms || answered.kind === current.kind && samePoints && JSON.stringify(answered.rubric) === JSON.stringify(current.rubric) && JSON.stringify(answered.scale ?? null) === JSON.stringify(current.scale ?? null)) return "";
  return a3Copy.answeredTermsSummary(row.attempt!.attempt_number == null ? a3Copy.recordedAgainst : a3Copy.attemptAnswered(row.attempt!.attempt_number), [answered.points != null && a3Copy.points3(fmt(Number(answered.points))), kindWords(answered.kind, answered.rubric)].filter(Boolean).join(" · "));
}

const number = (value: string) => value.trim() === "" ? null : Number(value);
const fmt = (value: number) => Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/0$/, "");

export function kindWords(kind: AssessmentKind, rubric: RubricCriterion[] | null | undefined) {
  if (kind === "completion") return a3Copy.completion;
  if (kind === "external_quiz") return a3Copy.quizCompletedElsewhere;
  if (kind === "in_class") return a3Copy.inClass;
  return rubric?.length ? a3Copy.scoredWithARubric : a3Copy.scored;
}

/** "Due Thu, Oct 22, 9:29 AM · Past due · 10 points · Quiz, completed elsewhere · Counts toward: Quizzes". */
export function workLine(parts: { due: string | null; pastDue: boolean; points: number | null; kind: AssessmentKind; rubric?: RubricCriterion[] | null; line: string | null }) {
  return [
    parts.due && a3Copy.due2(parts.due),
    parts.pastDue && a3Copy.pastDue,
    parts.points != null && a3Copy.points3(fmt(Number(parts.points))),
    kindWords(parts.kind, parts.rubric),
    parts.line && a3Copy.countsToward(parts.line),
  ].filter(Boolean).join(" · ");
}

export const released = (judgement: JudgementView | null, _now?: number) => judgement?.visible_at != null;

/** Whether the learner's enrollment takes new marks: a completed learner's work is read-only (#1384). */
export const markable = (row: Pick<ReviewLearnerRow, "markable">) => row.markable !== false;

/** A row waits for review when its latest attempt has no judgement yet and its learner can still be marked. */
export const waitsForReview = (row: ReviewLearnerRow) => markable(row) && !!row.attempt && !row.standing;

/** The enrollment, said quietly on the row when it is not the ordinary one. */
export function enrollmentWord(row: Pick<ReviewLearnerRow, "status" | "markable">) {
  if (row.status === "withdrawn") return "Withdrawn · read-only";
  if (row.status === "incomplete") return "Incomplete";
  if (!markable(row)) return "Completed the course · read-only";
  return null;
}

/** Why a learner's work takes no new marks; null when it does. */
export const readOnlySentence = (row: Pick<ReviewLearnerRow, "markable" | "display_name">) => markable(row) ? null
  : `${row.display_name} has completed the course, so their work here is read-only. Marks and feedback already recorded stay as they are.`;

/**
 * The judgement part's state: the judgement on record, by where it stands
 * ("Recorded, held", "Released", "Returned"); "Not recorded" only while the
 * teacher's own entry differs from it, or nothing is on record yet.
 */
export function judgementState(standing: JudgementView | null, kept: boolean): BoxState {
  if (kept || !standing) return { class: "draft", word: a3Copy.notRecorded };
  if (!released(standing)) return { class: "done", word: a3Copy.recordedHeldState };
  return { class: "done", word: standing.outcome === "returned" ? a3Copy.returned : a3Copy.released };
}

export function outcomeWord(judgement: Pick<JudgementView, "outcome" | "score">, points: number | null) {
  if (judgement.outcome === "returned") return a3Copy.returnedForRevision;
  if (judgement.outcome === "complete") return a3Copy.complete;
  if (judgement.outcome === "missing") return a3Copy.missing;
  return judgement.score != null && points != null ? a3Copy.gradedOf(fmt(Number(judgement.score)), fmt(Number(points))) : a3Copy.graded;
}

/** The row's state word, the same table board 4 reads (agreement section 7). */
export function rowWord(row: ReviewLearnerRow, terms: ReviewTerms, now?: number) {
  if (row.standing) return a3Copy.outcomeReleaseSummary(outcomeWord(row.standing, terms.points), released(row.standing, now) ? a3Copy.released : a3Copy.notReleased2);
  if (row.attempt) return markable(row) ? a3Copy.toReview2 : a3Copy.notMarked;
  if (terms.kind === "in_class") return "";
  if (terms.kind === "external_quiz") return a3Copy.notTaken;
  return a3Copy.notHandedIn;
}

/** The attempt line: how the latest attempt reached IMAM. */
export function attemptLine(row: ReviewLearnerRow, terms: ReviewTerms, when: (instant: string) => string) {
  const attempt = row.attempt;
  if (!attempt) return "";
  if (attempt.attempt_kind === "recorded") return terms.kind === "in_class" ? a3Copy.assessedInClass : a3Copy.recordedByTheTeacherNoHandIn;
  if (attempt.attempt_kind === "attested") return a3Copy.recordedAttestationWhen(terms.kind === "completion" ? a3Copy.markedDone : a3Copy.taken, when(attempt.submitted_at));
  return a3Copy.handedIn4(attempt.attempt_number == null ? "" : a3Copy.attempt4(attempt.attempt_number), when(attempt.submitted_at), attempt.late ? a3Copy.late : "");
}

/** A former recipient's line: withdrawal ended their access; what they received and handed in stays (S2A-1). */
export function formerLine(row: FormerRecipientRow, terms: ReviewTerms, when: (instant: string) => string) {
  const learner: ReviewLearnerRow = { ...row, status: undefined, markable: false, not_handed_in: false, line: null };
  return ["Withdrawn · current course access ended", attemptLine(learner, terms, when) || "Nothing handed in", row.standing ? rowWord(learner, terms) : null].filter(Boolean).join(" · ");
}

/** "n of m handed in", each way of arriving counted apart. */
export function handedInLine(rows: ReviewRow[], kind: AssessmentKind) {
  const count = (test: (row: ReviewRow) => boolean) => rows.filter(test).length;
  const rowKind = (row: ReviewRow) => row.attempt_terms?.assessment_kind ?? kind;
  const assessed = count(row => row.attempt?.attempt_kind === "recorded" && row.standing?.outcome !== "missing" && rowKind(row) === "in_class");
  const recorded = count(row => row.attempt?.attempt_kind === "recorded" && row.standing?.outcome !== "missing" && rowKind(row) !== "in_class");
  const missing = count(row => row.standing?.outcome === "missing");
  const done = count(row => row.attempt?.attempt_kind === "attested" && rowKind(row) === "completion");
  const taken = count(row => row.attempt?.attempt_kind === "attested" && rowKind(row) !== "completion");
  const handed = count(row => !!row.attempt && (row.attempt.attempt_kind ?? "handed_in") === "handed_in");
  const parts = [kind === "in_class" ? a3Copy.ofAssessedInClass(assessed, rows.length)
    : kind === "external_quiz" ? a3Copy.ofTaken(taken, rows.length) : a3Copy.ofHandedIn(handed, rows.length),
    done && a3Copy.markedDone2(done), recorded && a3Copy.recordedByTheTeacher(recorded),
    kind !== "in_class" && assessed && a3Copy.assessedInClass3(assessed),
    kind !== "external_quiz" && taken && `${taken} taken`,
    (kind === "in_class" || kind === "external_quiz") && handed && a3Copy.handedIn5(handed)];
  if (missing) parts.push(a3Copy.markedMissing(missing));
  return parts.filter(Boolean).join(" · ");
}

/** Up to three names not handed in, then a count. */
export function notHandedInNames(rows: ReviewLearnerRow[], kind: AssessmentKind) {
  if (kind === "in_class") return "";
  const names = rows.filter(row => !row.attempt).map(row => row.display_name);
  if (!names.length) return "";
  const shown = names.slice(0, 3).join(", ");
  return a3Copy.missingLearnersSummary(kind === "external_quiz" ? a3Copy.notTaken : a3Copy.notHandedIn, shown, names.length > 3 ? a3Copy.andMore(names.length - 3) : "");
}

export function scoreRefusal(value: string, points: number | null) {
  const score = number(value);
  if (score == null) return null;
  if (!Number.isFinite(score) || score < 0) return a3Copy.enterAScoreOf0OrMore;
  if (points != null && score > Number(points)) return a3Copy.moreThanPoints(fmt(Number(points)));
  return null;
}

/** Scored (no rubric), quiz and in-class work take a score cell on a folded row with no judgement. */
export const takesScoreCell = (row: ReviewLearnerRow, terms: ReviewTerms) =>
  terms.kind !== "completion" && terms.kind !== "native_quiz" && !terms.scale && !terms.rubric?.length && !row.standing;

/** A draft the column can carry: a score and nothing else. Anything more stays on its row. */
export function draftShape(draft: ReviewDraft | undefined): "empty" | "score" | "full" {
  if (!draft) return "empty";
  const more = draft.outcome && draft.outcome !== "graded" || draft.body.trim() || !draft.release
    || draft.selectedScaleRowId || Object.values(draft.criteria).some(value => value.trim())
    || Object.keys(draft.levels ?? {}).length || Object.keys(draft.quiz?.awards ?? {}).length;
  if (more) return "full";
  return draft.score.trim() ? "score" : "empty";
}

/** How a folded full draft names itself on its row. */
export function draftWords(draft: ReviewDraft) {
  if (!draft.release && !draft.outcome && !draft.body.trim()) return a3Copy.releaseHeldNotRecorded;
  const what = draft.outcome === "returned" ? a3Copy.returnForRevision : draft.outcome === "complete" ? a3Copy.complete : draft.outcome === "graded" ? a3Copy.grade : a3Copy.judgement;
  return a3Copy.toReviewNotRecorded(what, draft.body.trim() ? a3Copy.withAComment : "", draft.release ? "" : a3Copy.releaseHeld);
}

/** The act takes its specific name once a choice is made. */
export function actName(draft: ReviewDraft, terms: ReviewTerms) {
  if (!draft.outcome) return a3Copy.recordJudgement;
  if (marksIndividually(draft, terms)) return a3Copy.recordMarkHeld;
  if (!draft.release && draft.outcome !== "missing") return a3Copy.recordNotReleased;
  if (draft.outcome === "returned") return a3Copy.returnForRevision;
  if (draft.outcome === "complete") return a3Copy.markComplete;
  if (draft.outcome === "missing") return a3Copy.markMissing;
  return terms.kind === "external_quiz" || terms.kind === "in_class" ? a3Copy.recordScore : a3Copy.recordGrade;
}

export function criteriaTotal(draft: ReviewDraft, rubric: RubricCriterion[]) {
  const values = rubric.map(criterion => number(draft.criteria[criterion.id] ?? ""));
  return values.every(value => value != null && Number.isFinite(value)) ? values.reduce<number>((sum, value) => sum + (value ?? 0), 0) : null;
}

/** Why the act cannot be sent yet, or null when it can. */
export function draftBlocker(draft: ReviewDraft, terms: ReviewTerms) {
  if (!draft.outcome) return a3Copy.chooseHowToJudgeItFirst;
  if (terms.kind === "native_quiz") return draft.outcome !== "graded" ? "Mark this quiz by its original question awards." : quizMarkProblem(draft.quiz);
  if (draft.outcome === "returned") {
    if (terms.returnRefused) return terms.returnRefused;
    if (!draft.body.trim()) return a3Copy.aReturnNeedsACommentSayingWhatToChange;
    return null;
  }
  if (draft.outcome !== "graded") return null;
  if (terms.scale) {
    try { resolveScale({ scale: terms.scale, points: terms.points }, draft.selectedScaleRowId); }
    catch (error) { return (error as Error).message; }
    return null;
  }
  if (hasLevels(terms.rubric)) return levelLines(terms.rubric!, draft.levels ?? {}, draft.awards ?? {}).find(line => line.problem)?.problem ?? null;
  if (terms.rubric?.length) {
    const negative = terms.rubric.find(criterion => (number(draft.criteria[criterion.id] ?? "") ?? 0) < 0);
    if (negative) return a3Copy.enterAScoreOf0OrMore2(negative.label);
    const over = terms.rubric.find(criterion => (number(draft.criteria[criterion.id] ?? "") ?? 0) > Number(criterion.points));
    if (over) return a3Copy.isMoreThanPoints(over.label, fmt(Number(over.points)));
    return criteriaTotal(draft, terms.rubric) == null ? a3Copy.scoreEveryCriterionFirst : null;
  }
  const refusal = scoreRefusal(draft.score, terms.points);
  if (refusal) return refusal;
  if (draft.score.trim() === "" && (terms.kind !== "scored" || !draft.body.trim())) return a3Copy.enterTheScoreFirst;
  return null;
}

/** The batch item a draft sends: an attempt when there is one, else the enrolled learner. */
export function judgementItem(row: ReviewLearnerRow, draft: ReviewDraft, terms: ReviewTerms): JudgementItem {
  const target = row.attempt ? { submission_id: row.attempt.id } : { enrollment_id: row.enrollment_id };
  if (draft.outcome === "graded" && terms.scale) {
    return { ...target, outcome: "graded", body: draft.body.trim() || null, score: null, selected_scale_row_id: draft.selectedScaleRowId || null } as JudgementItem;
  }
  const criteria = draft.outcome === "graded" && terms.rubric?.length
    ? Object.fromEntries(terms.rubric.map(criterion => [criterion.id, Number(draft.criteria[criterion.id])]))
    : null;
  const score = draft.outcome === "graded" ? criteria ? criteriaTotal(draft, terms.rubric ?? []) : number(draft.score) : null;
  return { ...target, outcome: draft.outcome ?? undefined, body: draft.body.trim() || null, score, ...(criteria ? { criterion_scores: criteria } : {}) } as JudgementItem;
}

/** Who releases this work, for the teaching team: names, or the primary's role while unnamed. */
export function releaseNames(people: ReleaseLinePerson[] | undefined) {
  const names = (people ?? []).map(who => who.name ?? (who.role === "primary" ? a3Copy.thePrimaryInstructor : a3Copy.aGrantedMarker));
  return names.length ? names.join(a3Copy.or) : a3Copy.thePrimaryInstructor;
}

/** The exact held judgement a release names for this row, or null when it has none to release. */
export function releaseChoice(row: ReviewLearnerRow): ReleaseChoice | null {
  return row.status !== "withdrawn" && row.attempt && row.standing && !released(row.standing) ? { submission_id: row.attempt.id, feedback_id: row.standing.id } : null;
}

/**
 * A release round's receipt: the exact attempts released ("Fatima, attempt 1"), and every
 * other latest attempt it left out with the reason ("Not in this release: Yusuf, attempt 1 (not marked)").
 */
export function releaseWords(result: ReleaseResult | ReleaseRound, rows: ReviewLearnerRow[]) {
  const bySubmission = new Map(rows.flatMap(row => row.attempt ? [[row.attempt.id, row] as const] : []));
  const byEnrollment = new Map(rows.map(row => [row.enrollment_id, row.display_name] as const));
  const named = (id: string) => bySubmission.get(id)?.display_name ?? a3Copy.thisLearner;
  // A round read back names its learner and attempt itself, so an attempt a later one replaced still reads.
  const items = "items" in result ? result.items : result.included;
  const learner = (item: (typeof items)[number]) => (item.enrollment_id && byEnrollment.get(item.enrollment_id)) || named(item.submission_id);
  const numberOf = (item: (typeof items)[number]) => item.attempt_number ?? bySubmission.get(item.submission_id)?.attempt?.attempt_number ?? null;
  const to = items.map(item => {
    const number = numberOf(item);
    return `${learner(item)}${number == null ? "" : a3Copy.attemptN(number)}${item.authority === "primary_cover" ? a3Copy.inTheMarkerSPlace : ""}`;
  });
  // The receipt keeps the account written at the release (TQR-23).
  const heldIds = new Set("held" in result ? result.held ?? [] : []);
  const enrollmentOf = (item: (typeof items)[number]) => item.enrollment_id ?? bySubmission.get(item.submission_id)?.enrollment_id;
  const held = items.filter(item => heldIds.has(enrollmentOf(item) ?? "")).map(item => a3Copy.heldInReceipt(learner(item), numberOf(item)));
  const sent = to.map(name => `${a3Copy.releasedTo(name)}.`);
  const leftOut = result.not_included.map(item => a3Copy.notInThisRelease(
    byEnrollment.get(item.enrollment_id) ?? named(item.submission_id),
    item.attempt_number == null ? "" : a3Copy.attemptN(item.attempt_number),
    item.reason === "not_marked" ? a3Copy.notMarked2 : a3Copy.heldNotChosen));
  return { released: sent, leftOut, to, held };
}

/** Who released a round, as the teaching team reads it. */
export function releaserWord(round: Pick<ReleaseRound, "actor_name" | "actor_user_id">, me: string | null | undefined) {
  return round.actor_name ?? (round.actor_user_id === me ? a3Copy.you : a3Copy.aTeacher);
}

/** One round on one line of history: its number, who released it, when, and the exact attempts. */
export function roundWords(round: ReleaseRound, rows: ReviewLearnerRow[], when: string, me: string | null | undefined, last: boolean) {
  return (last ? a3Copy.lastRelease : a3Copy.earlierRelease)(round.round, releaserWord(round, me), when, releaseWords(round, rows).to.join("; "));
}

/**
 * Where current work stands for release (Step 5 R1): what is ready and who releases it;
 * else the attempt still waiting and why, or how many; else what is released.
 */
export function releaseStanding(ready: number, waiting: { row: ReviewLearnerRow; why: string }[], releasedCount: number, releasers: string) {
  if (ready) return `${a3Copy.readyToRelease(ready)} · ${a3Copy.releasesIt(releasers)}`;
  if (waiting.length === 1) {
    const [{ row, why }] = waiting, number = row.attempt?.attempt_number;
    return `${a3Copy.nothingReadyToRelease} · ${a3Copy.attemptWaits(row.display_name, number == null ? "" : a3Copy.attemptN(number), why)}`;
  }
  if (waiting.length) return `${a3Copy.nothingReadyToRelease} · ${a3Copy.attemptsNotReady(waiting.length)}`;
  return releasedCount ? a3Copy.releasedNothingElseReady(releasedCount) : a3Copy.nothingReadyToReleaseYet;
}

/** The one line a learner's figure reads, for the teacher. */
export function figureWords(name: string, line: string | null, figure: ReviewLearnerRow["line"]) {
  if (!line || !figure) return "";
  const base = figure.mean == null ? a3Copy.sSoFarNothingReleasedYet(name, line)
    : a3Copy.sSoFarFromReleasedScore(name, line, Math.round(figure.mean), figure.released_count, figure.released_count === 1 ? "" : "s");
  return figure.not_handed_in_count ? a3Copy.notHandedInNotCountedYet(base, figure.not_handed_in_count) : base;
}

/** What Mark missing does to the figure: the 0 and the new mean. */
export function missingEffect(name: string, line: string | null, figure: ReviewLearnerRow["line"], canAttemptAgain = true) {
  const count = figure?.released_count ?? 0;
  const mean = figure?.mean ?? 0;
  const next = Math.round(count ? mean * count / (count + 1) : 0);
  return a3Copy.readsMissingMarkedByYourInstructorItCounts0(name, line ? a3Copy.onWhichBecomes(line, next) : "", canAttemptAgain ? a3Copy.whileTheWorkIsOpenCanStillHandIt(name) : "");
}

/** One sentence above the act: what the learner gets (agreement section 8). */
export function effectSentence(row: ReviewLearnerRow, draft: ReviewDraft, terms: ReviewTerms, canAttemptAgain: boolean) {
  const name = row.display_name;
  if (!draft.outcome) return "";
  if (draft.outcome === "missing") return "";
  if (!draft.release) {
    const reads = row.released ? outcomeWord(row.released, terms.points) : row.attempt ? a3Copy.handedIn2 : a3Copy.nothingYet;
    return a3Copy.recordedNotReleasedStillReads(name, reads);
  }
  if (draft.outcome === "returned") return a3Copy.readsYourComment(name, canAttemptAgain ? a3Copy.andCanHandInAttempt((row.attempt?.attempt_number ?? 0) + 1) : "");
  if (draft.outcome === "complete") return a3Copy.readsComplete(name, draft.body.trim() ? a3Copy.andYourComment : "");
  const selected = terms.scale?.rows.find(row => row.id === draft.selectedScaleRowId);
  const score = terms.scale ? selected ? Number(selected.score) : null : terms.rubric?.length ? criteriaTotal(draft, terms.rubric) : number(draft.score);
  const word = score != null && terms.points != null ? a3Copy.gradedOf(fmt(score), fmt(Number(terms.points))) : a3Copy.graded;
  return a3Copy.reads(name, word, draft.body.trim() ? a3Copy.andYourComment : "");
}

export type ItemOutcome = JudgementItemResult["result"] | "not_confirmed";

/** Receipt words per learner, from b8a's per-item results. */
export function itemWords(result: ItemOutcome, reason: string | null) {
  if (result === "recorded_released") return a3Copy.recordedAndReleased;
  if (result === "recorded_not_released") return a3Copy.recordedNotReleased;
  if (result === "not_confirmed") return a3Copy.notConfirmedCheckAgainBeforeSendingItAgain;
  return a3Copy.notRecordedYourEntryIsKept(reason ? ` (${reason.replace(/\.$/, "")})` : "");
}

export function batchReceipt(results: { name: string; result: ItemOutcome; reason: string | null }[]) {
  return results.map(item => `${item.name}: ${itemWords(item.result, item.reason)}.`).join(" ");
}

export const plural = (count: number, one: string, many = a3Copy.s(one)) => a3Copy.countedWord(count, count === 1 ? one : many);
